import { useState, useRef, useEffect, useCallback } from 'react';
import MicIcon from '@mui/icons-material/Mic';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { submitAnswer } from '../../../utilities/Answer';
import { newMessage, updateSingleMessage } from '../../../actions';
import { updateAudioInputsState, updateEnvironment } from '../../../actions/environment.action';
import { stopVoiceOutput } from '../../../services/shared';
import { emit } from '../../../services/socket';
import { playAudioBuffer } from '../../../services/saveResponse';
import { STORE } from '../../../store';

export default function Audiov4() {
  const [transcript, setTranscript] = useState('');
  const [mid, setMid] = useState(uuid());
  const midRef = useRef(mid);

  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState('');

  // Audio processing refs (exactly like your working code)
  const audioContextRef = useRef<AudioContext | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // for speaker
  const audioContext: any = useRef<AudioContext | null>(null);
  const scheduledAudioSources: any = useRef([]);
  const agentVoiceAnalyser: any = useRef(null);
  const startTimeRef = useRef(-1);
  const isVoiceOutputPlaying = useRef(false);
  const voiceOutputEndedCallback = useRef<(() => void) | null>(null);

  // Add at the top of the component:
  const autoSubmitTimeout = useRef<NodeJS.Timeout | null>(null);
  const transcriptRef = useRef('');

  const environment = useSelector((state: any) => state?.environment);

  const activeQuestion = useSelector((state: any) => state?.environment.activeQuestion);
  const isListening = isTranscribing;
  const aiRepliesCount = useSelector((state: any) => state.ai?.aiReplies) || 0;

  const dispatch = useDispatch();

  /**
    * Initialize the audio context for managing and playing audio. (just for TTS playback; user audio input logic found in Microphone Context Provider)
    */
  useEffect(() => {
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)({
        latencyHint: "interactive",
        sampleRate: 24000,
      });
      agentVoiceAnalyser.current = audioContext.current.createAnalyser();
      agentVoiceAnalyser.current.fftSize = 2048;
      agentVoiceAnalyser.current.smoothingTimeConstant = 0.96;
    }
  }, []);

  // Update midRef when mid changes
  useEffect(() => {
    midRef.current = mid;
  }, [mid]);

  // Socket event listeners for transcription (exactly like your working code)
  useEffect(() => {
    const handleTranscriptionResult = (event: any) => {
      const data = event.detail;
      if (data.sessionId !== midRef.current) return;

      const received = data.result;

      if (received.channel && received.channel.alternatives && received.channel.alternatives[0]) {
        const transcriptText = received.channel.alternatives[0].transcript;
        if (transcriptText && transcriptText.trim()) {
          setTranscript(prev => {
            const newTranscript = (prev + ' ' + transcriptText).trim();
            transcriptRef.current = newTranscript;

            addMessageWithMid(newTranscript, midRef.current);
            return newTranscript;
          });
          // Reset auto-submit timer
          if (autoSubmitTimeout.current) clearTimeout(autoSubmitTimeout.current);
          autoSubmitTimeout.current = setTimeout(() => {
            // Auto-submit after 3 seconds of inactivity
            stopTranscription();
            addMessageWithMid(transcriptRef.current, midRef.current);
            submitAnswer(transcriptRef.current, '', null, false, '', midRef.current);
            setTranscript('');
            transcriptRef.current = '';
            setMid(uuid());
          }, 5000);
        }
      }
    };

    const handleTranscriptionError = (event: any) => {
      const data = event.detail;
      if (data.sessionId !== midRef.current) return;
      console.error('Backend transcription error:', data.error);

      setError(data.error);
      setIsTranscribing(false);
      dispatch(updateAudioInputsState({
        isMicOn: false,
        isListening: false
      }));
    };

    const bufferAudio = (data: any) => {
      const arrayBuffer = data.detail;
      isVoiceOutputPlaying.current = true;
      
      audioContext.current.decodeAudioData(arrayBuffer)
        .then((audioBuffer: AudioBuffer) => {
          if (!audioBuffer) return;

          const source = playAudioBuffer(
            audioContext.current,
            audioBuffer,
            startTimeRef,
            agentVoiceAnalyser.current
          );

          // Add event listener to detect when this audio source ends
          source.onended = () => {
            // Remove this source from the array
            const index = scheduledAudioSources.current.indexOf(source);
            if (index > -1) {
              scheduledAudioSources.current.splice(index, 1);
            }
            
            // Check if all audio sources have finished
            if (scheduledAudioSources.current.length === 0) {
              isVoiceOutputPlaying.current = false;
              
              // Trigger callback if set
              if (voiceOutputEndedCallback.current) {
                voiceOutputEndedCallback.current();
              }
              
              // Dispatch custom event
             dispatch(updateEnvironment({
              isVoiceOutputEnabled: false
             }));
            }
          };

          scheduledAudioSources.current.push(source);
        })
        .catch((error: any) => {
          console.log('Error while decoding audio:', error);
          isVoiceOutputPlaying.current = false;
        });
    };

    const clearAudioBuffer = () => {
      scheduledAudioSources.current.forEach((source: any) => source.stop());
      scheduledAudioSources.current = [];
      startTimeRef.current = -1; // Reset start time
      isVoiceOutputPlaying.current = false;
    };

    const handleVoiceOutputEnded = () => {
      console.log('Voice output ended - you can add custom logic here');
    };

    // Add event listeners to window
    window.addEventListener('transcription-result', handleTranscriptionResult);
    window.addEventListener('transcription-error', handleTranscriptionError);
    window.addEventListener('audio-generated', bufferAudio);
    window.addEventListener('clear-audio', clearAudioBuffer);
    window.addEventListener('voice-output-ended', handleVoiceOutputEnded);


    return () => {
      // Cleanup listeners
      window.removeEventListener('transcription-result', handleTranscriptionResult);
      window.removeEventListener('transcription-error', handleTranscriptionError);
      window.removeEventListener('audio-generated', bufferAudio);
      window.removeEventListener('clear-audio', clearAudioBuffer);
      window.removeEventListener('voice-output-ended', handleVoiceOutputEnded);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);



  // Audio capture function (exactly like your working code)
  const startAudioCapture = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        }
      });

      streamRef.current = stream;
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 16000
      });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = scriptProcessor;

      scriptProcessor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer.getChannelData(0);
        // Convert Float32Array to Int16Array for Deepgram (linear16)
        const int16Data = new Int16Array(inputBuffer.length);
        for (let i = 0; i < inputBuffer.length; i++) {
          const sample = Math.max(-1, Math.min(1, inputBuffer[i]));
          int16Data[i] = sample * 0x7FFF;
        }

        // Send audio data to backend via WebSocket
        emit('audio-chunk', {
          sessionId: midRef.current,
          audioData: int16Data.buffer
        });
      };

      source.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);

    } catch (error) {
      console.error('Error capturing audio:', error);
      setError('Microphone access denied. Please allow microphone access and refresh.');
    }
  };

  // Main transcription toggle function
  const handleTranscriptionToggle = async () => {
    if (isTranscribing) {
      stopTranscription();
    } else {
      startTranscription();
    }
  };

  const startTranscription = async () => {
    try {
      // Generate new mid for this transcription session
      const newMid = uuid();
      setMid(newMid);
      setTranscript('');
      setError('');

      // Start backend transcription session
      emit('start-transcription', { sessionId: newMid });

      // Start audio capture
      await startAudioCapture();

      setIsTranscribing(true);
      dispatch(updateAudioInputsState({
        isMicOn: true,
        isListening: true
      }));

    } catch (err) {
      console.error('Failed to start backend transcription:', err);
      setError('Failed to start transcription. Check console for details.');
    }
  };

  const stopTranscription = () => {
    // Clear auto-submit timeout
    if (autoSubmitTimeout.current) {
      clearTimeout(autoSubmitTimeout.current);
      autoSubmitTimeout.current = null;
    }

    // Stop backend transcription
    emit('stop-transcription', { sessionId: midRef.current });

    // Cleanup audio resources
    cleanup();

    setIsTranscribing(false);
    dispatch(updateAudioInputsState({
      isMicOn: false,
      isListening: false
    }));
  };

  const cleanup = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
  };

  const startListening = useCallback(async () => {
    await handleTranscriptionToggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMessageWithMid = useCallback((transcript: string, messageMid: string) => {
    if (!transcript || !transcript.trim()) return;
    
    // Get the latest messages state directly from STORE
    const currentState = STORE.getState();
    const currentMessages = currentState.messages || [];
    
    const exists = currentMessages.find((message: any) => message.mid === messageMid);
    
    stopVoiceOutput();
    
    if (exists) {
      dispatch(updateSingleMessage({
        label: transcript.trim(),
        position: 'right',
        type: 'STATEMENT',
        mid: messageMid,
        questionId: activeQuestion?.id,
        isTemp: true
      }));
    } else {
      dispatch(newMessage({
        label: transcript.trim(),
        position: 'right',
        type: 'STATEMENT',
        mid: messageMid,
        questionId: activeQuestion?.id,
        isTemp: true
      }));
    }
  }, [dispatch, activeQuestion?.id]);

  // Auto-start when voice output is enabled
  useEffect(() => {
    if (environment?.isVoiceOutputEnabled && !isTranscribing && aiRepliesCount > 0) {
      startListening()
    }
  }, [environment?.isVoiceOutputEnabled, isTranscribing, startListening, aiRepliesCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (isTranscribing) {
        emit('stop-transcription', { sessionId: midRef.current });
        setIsTranscribing(false);
      }
      if (autoSubmitTimeout.current) {
        clearTimeout(autoSubmitTimeout.current)
      };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='voice-input-container flex flex-col justify-center items-center h-voice'>
      <div className="relative">
        <button
          id="speech"
          className={`btn relative rounded-full p-4 shadow-lg transition-all duration-300 transform ${
            environment?.typing 
              ? 'bg-secondaryBgColor cursor-not-allowed opacity-50' 
              : isListening 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 animate-pulse' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 hover:scale-110'
          } group`}
          onClick={() => {
            if (environment?.typing) return; // Don't allow interaction when typing/paused
            
            if (isListening) {
              stopTranscription();
            } else {
              startListening();
              stopVoiceOutput();
            }
          }}
          disabled={environment?.typing}
          title={
            environment?.typing 
              ? "Please wait, AI is responding..." 
              : isListening 
                ? "Stop voice recording" 
                : "Start voice recording"
          }
        >
          <MicIcon
            fontSize="large"
            sx={{
              color: "white",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
            }}
          />
          <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
        </button>
        
        {/* Wavy animation rings when listening */}
        {isListening && !environment?.typing && (
          <>
            <div className="absolute inset-0 rounded-full border-2 border-blue-300 animate-ping"></div>
            <div className="absolute inset-0 rounded-full border-2 border-indigo-300 animate-pulse" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute -inset-2 rounded-full border-2 border-blue-200 animate-ping" style={{animationDelay: '1s'}}></div>
          </>
        )}
      </div>
      
      <p className="text-secondaryText my-2 mb-0">
        {environment?.typing 
          ? "AI is responding..." 
          : isListening 
            ? "Listening..." 
            : "Tap to Speak"
        }
      </p>
      
      {error && (
        <p className="text-red-500 text-xs mt-1 text-center">{error}</p>
      )}
      
      {transcript && (
        <div className="mt-2 text-center">
          <p className="text-sm text-gray-600 italic">"{transcript}"</p>
        </div>
      )}
    </div>
  );
}
